/**
 * @file PickItemRowMenu.tsx
 * @description Меню «⋯» строки задания на сборку (phase19.24, довесок шага 4).
 *
 * Вынесено из `PickItemsTable`, когда у строки появился третий исход: таблица
 * отвечает за колонки, меню — за реестр действий. Набор пунктов зависит только
 * от состояния строки, и решение принимается здесь один раз, а не ветками в
 * разметке (docs/processes/engineering-principles.md, OCP).
 *
 * | Состояние строки | Пункты |
 * | --- | --- |
 * | обычная | «Ввести количество», «Отметить недостачу» |
 * | с недостачей | «Снять отметку» (сервер: `shortageQty: 0`) |
 * | задание закрыто/чужое (`editable = false`) | меню не рисуется |
 *
 * Задание в статусе `SHORTAGE` сюда не попадает: там `editable = false` —
 * строки правятся только после возврата задания в очередь (409
 * `picking_shortage_declared`).
 */

import { AlertTriangle, Pencil, RotateCcw } from "lucide-react";

import { RowActionsMenu, type RowAction } from "../../ui/RowActionsMenu";
import type { PickingTaskItemDto } from "../../../lib/api-picking";

interface Props {
  item: PickingTaskItemDto;
  /** false — задание закрыто или чужое: действий по строке нет. */
  editable: boolean;
  busy: boolean;
  onOpenManualQty: () => void;
  onOpenShortage: () => void;
  onClearShortage: () => void;
}

export function PickItemRowMenu({
  item,
  editable,
  busy,
  onOpenManualQty,
  onOpenShortage,
  onClearShortage,
}: Props) {
  const shortage = Number(item.shortageQty) > 0;
  const actions: RowAction[] = !editable
    ? []
    : shortage
      ? [
          {
            key: "clear-shortage",
            label: "Снять отметку",
            icon: RotateCcw,
            disabled: busy,
            run: onClearShortage,
          },
        ]
      : [
          {
            key: "qty",
            label: "Ввести количество",
            icon: Pencil,
            disabled: busy,
            run: onOpenManualQty,
          },
          {
            key: "shortage",
            label: "Отметить недостачу",
            icon: AlertTriangle,
            danger: true,
            disabled: busy,
            run: onOpenShortage,
          },
        ];

  return (
    <RowActionsMenu
      actions={actions}
      title={`Действия с позицией ${item.skuCode}`}
      ariaLabel={`Действия с позицией ${item.skuCode}`}
      disabled={busy}
    />
  );
}
